import React, { useState, useEffect } from 'react'
import axios from 'axios'
import ManualPropertyForm from '../components/ManualPropertyForm'

function LandingPage({ onStartAnalysis, onViewMap, onViewPortfolio }) {
  const [config, setConfig] = useState(null)
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState(null)
  const [searchError, setSearchError] = useState(null)
  
  // Manual input state
  const [showManualForm, setShowManualForm] = useState(false)
  
  // Active tab: search or manual (real data only)
  const [activeTab, setActiveTab] = useState('search')

  useEffect(() => {
    fetchConfig()
  }, [])

  const fetchConfig = async () => {
    try {
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const response = await axios.get(`${API_BASE}/api/v1/config`)
      setConfig(response.data)
    } catch (error) {
      console.error('Failed to fetch config:', error)
    }
  }

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!searchQuery.trim()) return
    
    setIsSearching(true)
    setSearchError(null)
    setSearchResults(null)
    
    try {
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const response = await axios.post(`${API_BASE}/api/v1/properties/search`, {
        postcode: searchQuery.trim(),
        address: ''
      })
      
      const data = response.data
      
      if (data.source === 'search' && data.results && data.results.length > 0) {
        setSearchResults(data.results)
      } else {
        setSearchError('No properties found. Try entering details manually.')
      }
    } catch (error) {
      console.error('Search failed:', error)
      setSearchError(error.response?.data?.detail || 'Search failed. Please try again or enter details manually.')
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectSearchResult = async (result) => {
    try {
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const response = await axios.post(`${API_BASE}/api/v1/properties/select-search-result`, {
        result
      })
      
      if (response.data.success && response.data.property) {
        onStartAnalysis(response.data.property_key || response.data.property.id, {
          detailsUnavailable: response.data.details_unavailable === true
        })
      }
    } catch (error) {
      console.error('Failed to select result:', error)
      setSearchError('Failed to load property. Please try again.')
    }
  }

  const handleManualPropertyCreated = (property) => {
    // Property created, start analysis
    onStartAnalysis(property.id)
  }

  const getYieldColor = (yield_value) => {
    if (yield_value >= 6.5) return 'text-green-600'
    if (yield_value >= 5) return 'text-yellow-600'
    return 'text-orange-600'
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-navy-900 text-white py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">DealPack Pro</h1>
            <p className="text-gray-300 mt-1">AI-Powered Investment Analysis</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-400">12 hrs → 47 sec</div>
            <div className="text-sm text-gray-400">Analysis time</div>
            <div className="flex gap-2 mt-2 justify-end">
              {onViewMap && (
                <button
                  onClick={onViewMap}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                >
                  🗺️ View Map
                </button>
              )}
              {onViewPortfolio && (
                <button
                  onClick={onViewPortfolio}
                  className="px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm"
                >
                  📊 Portfolio
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Property Investment Analysis, <span className="text-primary-600">Amplified by AI</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our 5-agent AI system analyzes deals in real-time: Research, Valuation, Risk, 
              Compliance, and Underwriting — all working together to deliver institutional-grade 
              investment intelligence.
            </p>
            
            {config && (
              <div className="mt-6 flex justify-center gap-4 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-sm ${config.features.ai_agents ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {config.features.ai_agents ? '✓' : '○'} AI Agents
                </span>
                <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  ✓ Monte Carlo Simulation
                </span>
                <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  ✓ Real-time Analysis
                </span>
                {config.features.search && (
                  <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                    ✓ Property Search
                  </span>
                )}
                <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  ✓ Manual Input
                </span>
              </div>
            )}
          </div>

          {/* Input Method Tabs */}
          <div className="mb-8">
            <div className="flex justify-center gap-2 mb-8">
              <button
                onClick={() => setActiveTab('search')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'search' 
                    ? 'bg-primary-600 text-white shadow-lg' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border'
                }`}
              >
                🔍 Search by Postcode
              </button>
              <button
                onClick={() => setActiveTab('manual')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'manual' 
                    ? 'bg-primary-600 text-white shadow-lg' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border'
                }`}
              >
                ✏️ Manual Entry
              </button>
            </div>

            {/* Search Section */}
            {activeTab === 'search' && (
              <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Search for a Property</h3>
                  <p className="text-gray-600 mb-6">
                    Enter a UK postcode to search for properties in that area.
                  </p>
                  
                  <form onSubmit={handleSearch} className="flex gap-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Enter postcode (e.g., E15 1EL, SW1A 1AA)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSearching || !searchQuery.trim()}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isSearching ? (
                        <>
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Searching...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          Search
                        </>
                      )}
                    </button>
                  </form>

                  {/* Search Results */}
                  {searchResults && searchResults.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-900 mb-3">
                        Found {searchResults.length} properties
                      </h4>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {searchResults.map((result, index) => (
                          <div
                            key={result.id || index}
                            onClick={() => handleSelectSearchResult(result)}
                            className="p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 cursor-pointer transition-all"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-gray-900">{result.address}</p>
                                <p className="text-sm text-gray-500">{result.postcode}</p>
                                <div className="flex gap-4 mt-2 text-sm text-gray-600">
                                  <span>{result.bedrooms} bed</span>
                                  <span className="capitalize">{result.property_type}</span>
                                  <span>Sold: {result.transaction_date}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-primary-600">
                                  £{result.price?.toLocaleString()}
                                </p>
                                <button className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium">
                                  Analyze →
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Results */}
                  {searchError && (
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="text-yellow-800">{searchError}</p>
                          <button
                            onClick={() => {
                              setActiveTab('manual')
                              setSearchError(null)
                            }}
                            className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Switch to Manual Entry →
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Manual Entry Section */}
            {activeTab === 'manual' && (
              <div className="max-w-3xl mx-auto">
                <ManualPropertyForm
                  onPropertyCreated={handleManualPropertyCreated}
                  onCancel={() => setActiveTab('search')}
                />
              </div>
            )}
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">How It Works</h3>
            
            <div className="grid md:grid-cols-5 gap-4">
              {[
                { icon: '🔍', name: 'Research', desc: 'Land Registry, planning, demographics' },
                { icon: '💰', name: 'Valuation', desc: 'Comparable sales analysis' },
                { icon: '⚠️', name: 'Risk', desc: 'Monte Carlo simulation' },
                { icon: '📋', name: 'Compliance', desc: 'Regulatory checks' },
                { icon: '🎯', name: 'Underwriting', desc: 'Final recommendation' }
              ].map((agent, idx) => (
                <div key={agent.name} className="text-center relative">
                  <div className="text-3xl mb-2">{agent.icon}</div>
                  <div className="font-semibold text-gray-900">{agent.name}</div>
                  <div className="text-xs text-gray-500 mt-1">{agent.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="mt-12 grid md:grid-cols-4 gap-6 text-center">
            <div className="bg-primary-50 rounded-lg p-6">
              <div className="text-3xl font-bold text-primary-600">£50M+</div>
              <div className="text-gray-600">Deal Flow Analyzed</div>
            </div>
            <div className="bg-primary-50 rounded-lg p-6">
              <div className="text-3xl font-bold text-primary-600">90%</div>
              <div className="text-gray-600">Time Reduction</div>
            </div>
            <div className="bg-primary-50 rounded-lg p-6">
              <div className="text-3xl font-bold text-primary-600">£45K+</div>
              <div className="text-gray-600">Risk Exposure Caught</div>
            </div>
            <div className="bg-primary-50 rounded-lg p-6">
              <div className="text-3xl font-bold text-primary-600">5</div>
              <div className="text-gray-600">AI Agents</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-600">
          <p>DealPack Pro • Built with FastAPI, React, and OpenAI</p>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
